/* Adapted from https://github.com/christopherthielen/ui-router-extras/issues/371#issuecomment-331553096
* Per my preference, this uses an ES6 class for the service. I use the factory recipe rather than the service
* one because we need to add the transition hook. I'd use a static class method, but I can't create a $inject property for
* one of those.
* */

import angular from "angular";
import uiRouterModule from "@uirouter/angularjs";

const previousStateServiceName = "$previousState";

class PreviousStateService {
    constructor($state, $log) {
        this._stateSvc = $state;
        this._logSvc = $log;
        this._memos = new Map();
        this._previousState = null;
        this._lastPreviousState = null;
    }

    get(memoName) {
        return memoName ? this._memos.get(memoName) : this._previousState;
    }

    set(memoName, previousState, previousParams) {
        const updatedState = {
            state: this._stateSvc.get(previousState),
            params: previousParams
        };
        this._memos.set(memoName, updatedState);
        this._logSvc(`Previous State Service: Set memo '${memoName}' to `, updatedState);
    }

    go(memoName, options) {
        const to = this.get(memoName);
        if (!to) {
            return Promise.reject(new Error(`no previous state${memoName ? ` for memo: ${memoName}`: ""}`));
        }
        return this._stateSvc.go(to.state, to.params, options);
    }

    memo(memoName, defaultStateName, defaultStateParams) {
        this._memos.set(
            memoName,
            this._previousState || {
                state: this._stateSvc.get(defaultStateName),
                params: defaultStateParams
            });
    }

    forget(memoName) {
        if (memoName) {
            this._memos.delete(memoName);
        }
        else {
            this._previousState = undefined;
        }
    }

    updateService(currentTransition) {
        this._logSvc.debug("Updating Previous State Service");
        const fromState = currentTransition.from().$$state();
        const commit = () => {
            this._lastPreviousState = null;
            this._logSvc.debug("Committed Previous State Update. Previous State is ", this._previousState);
        };
        const revert = () => {
            this._previousState = this._lastPreviousState;
            this._logSvc.debug("Reverted Previous State Update. Previous State is ", this._previousState);
        };

        if (fromState) {
            this._lastPreviousState = this._previousState;
            this._previousState = {
                state: currentTransition.from(),
                params: currentTransition.params("from")
            };

            this._logSvc.debug("Prepared to update previous state to: ", this._previousState);

            currentTransition.promise
                             .then(commit)
                             .catch(revert);
        }
    }
}

function PreviousStateServiceFactory($state, $log, $transitions) {
    $log.debug("Building Previous State Service instance and setting hook.");
    $transitions.onStart({}, (transition) => {
        const prevStateService = transition.injector().get(previousStateServiceName);
        return prevStateService.updateService(transition);
    });

    return new PreviousStateService($state, $log);
}

PreviousStateServiceFactory.$inject = [
    "$state",
    "$log",
    "$transitions"
];

function InitializePreviousStateService($injector) {
    $injector.get(previousStateServiceName);
}
InitializePreviousStateService.$inject = ["$injector"];

export default angular
    .module("trackerbase-app.services.previous-state-service", [
        uiRouterModule
    ])
    .factory(previousStateServiceName, PreviousStateServiceFactory)
    .run(InitializePreviousStateService)
    .name;



//////////////////
// WEBPACK FOOTER
// ./src/services/PreviousStateService.js
// module id = 8+gR
// module chunks = 1